/**
 * MYS-7313: versioned confirmation stamp of a signed DCM contract.
 *
 * The stamp of a signed contract must never change afterwards (legal requirement), so a
 * layout version is written onto the contract once, at signing time, and every renderer
 * (api2 PDF, dashboard cards, lists, signing page) branches on it through these helpers.
 * A future legal change to the stamp = new version + new branch; old branches stay
 * untouched so an audit can read exactly what each version produced.
 *
 *   1  legacy: template name heading, "signed on … by <person>", issued-on line
 *   2  official contract name heading, "signed on … von <Unternehmen> durch die
 *      zeichnungsberechtigte Person <person>", no issued-on line
 *
 * Dependency-free on purpose: imported by api2 (ts-node at runtime) and by the dashboard.
 */
export const CURRENT_SIGNATURE_VERSION = 2

const LEGACY_SIGNATURE_VERSION = 1

export interface StampedContract {
	signatureVersion?: number | null
	officialName?: Record<string, string> | null
}

/** Contracts written before MYS-7313 have no version (or 1) and keep the legacy stamp, issued-on line included. */
export function usesLegacyStamp(contract: StampedContract | null | undefined): boolean {
	return (contract?.signatureVersion ?? LEGACY_SIGNATURE_VERSION) < 2
}

/**
 * Heading of the stamp. `templateName` is the internal template name the caller already
 * resolved for its locale; legacy stamps always show it, v2 stamps prefer the official-name
 * snapshot (requested locale, else any locale, else the template name).
 */
export function stampHeading(contract: StampedContract | null | undefined, locale: string, templateName: string): string {
	if (usesLegacyStamp(contract)) return templateName
	const snapshot = contract?.officialName || {}
	const inLocale = typeof snapshot[locale] === 'string' ? snapshot[locale].trim() : ''
	if (inLocale) return inLocale
	const anyLocaleValue = Object.values(snapshot).find(v => typeof v === 'string' && v.trim())
	return anyLocaleValue ? anyLocaleValue.trim() : templateName
}
